import classes from "./styles.module.css";
import { Button, CircularProgress, TextField } from "@mui/material";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import axios from "axios";
import { path } from "../../../routes";
import { useEffect, useMemo, useState } from "react";

const getDisplayBaseUrl = () => {
  if (typeof window === "undefined") return "";
  // /admin/ prefix konzistentně s admin Zobrazovače / UsersDisplays /
  // referenční XLSX (Odkaz zařízení).
  return `${window.location.origin}/admin/`;
};

const HOLIDAY_INN_ORG_ID = "E15F8010-B7EB-4526-A985-F00D517EBF4C";
const VIENNA_HOUSE_ORG_ID = "62BDEE7F-9CE9-41BE-A80C-EF69F045D56C";
// Legacy hotely — XLSX-dokumentovaný dlouhý URL s orgID. Nové orgy = krátký URL.
const LEGACY_ORG_IDS = [
  HOLIDAY_INN_ORG_ID,
  "AC9D7FD5-E333-4B1E-9043-4697568313B7",
  VIENNA_HOUSE_ORG_ID,
  "82BB6005-2C25-4EB9-A6CC-DB8BFB4A3C70",
  "7A67452A-F5D4-430D-B283-8A93B28B425C",
].map(s => s.toUpperCase());

/** Pro Holiday Inn rozhodne id/room/orgID podle description / url_id.
 *  Vrací objekt — caller pak složí URL ve správném pořadí. */
const resolveHolidayInnParams = (row) => {
  const desc = String(row?.description || '').toLowerCase().trim();
  const urlId = row?.url_id != null ? String(row.url_id).trim() : '';
  const urlRoom = row?.url_room != null ? String(row.url_room).trim() : '';
  const dg = Number(row?.device_group);

  // url_id má prioritu (synthetic + explicit override v DB)
  if (urlId) {
    if (urlId === '1' || urlId === '9') return { id: urlId, room: null, orgID: null };
    if (urlId === '3') return { id: '3', room: null, orgID: HOLIDAY_INN_ORG_ID };
    if (urlId === '12' || urlId === '13') return { id: urlId, room: null, orgID: HOLIDAY_INN_ORG_ID };
    if (urlId === '11') return { id: '11', room: urlRoom || String(row.id), orgID: null };
    if (urlId === '101') return { id: '101', room: urlRoom || String(row.id), orgID: null };
    return { id: urlId, room: urlRoom || null, orgID: null };
  }

  // Bez url_id: odvodit z description / device_group
  if (desc === 'a-d' || desc === 'a - d') return { id: '12', room: null, orgID: HOLIDAY_INN_ORG_ID };
  if (desc === 'e-f' || desc === 'e - f') return { id: '13', room: null, orgID: HOLIDAY_INN_ORG_ID };
  if (desc === 'foyer') return { id: '100', room: '29', orgID: null };
  if (desc.startsWith('meeting room a')) return { id: '11', room: '23', orgID: null };
  if (desc.startsWith('meeting room b')) return { id: '11', room: '24', orgID: null };
  if (desc.startsWith('meeting room c')) return { id: '11', room: '25', orgID: null };
  if (desc.startsWith('meeting room d')) return { id: '11', room: '26', orgID: null };
  // Default — displej (dg=1) → id=11&room=<deviceId>; kiosek → id=100&room=<deviceId>
  return dg === 1
    ? { id: '11', room: String(row.id), orgID: null }
    : { id: '100', room: String(row.id), orgID: null };
};

const buildDeviceUrl = (base, row) => {
  const orgId = row?.cOrganization_id;
  const normOrg = String(orgId || "").replace(/[{}]/g, "").toUpperCase();
  const isHolidayInn = normOrg === HOLIDAY_INN_ORG_ID;
  const isViennaHouse = normOrg === VIENNA_HOUSE_ORG_ID;
  const dg = Number(row?.device_group);

  // 1) Vienna House Plzeň kiosek (dg=2) → ?id=6&kiosk=1/2 podle zařízení
  if (isViennaHouse && dg === 2) {
    const desc = String(row?.description || '').toLowerCase();
    let kiosk = '2';
    if (row?.url_kiosk != null && String(row.url_kiosk).trim() !== '') {
      kiosk = String(row.url_kiosk).trim();
    } else if (Number(row.id) === 40 || desc.includes('hall') || desc.includes('dolní')) {
      kiosk = '1';
    } else if (Number(row.id) === 41 || desc.includes('1st floor') || desc.includes('horní')) {
      kiosk = '2';
    }
    return `${base}?id=6&kiosk=${kiosk}`;
  }

  // 2) Holiday Inn — pořadí parametrů per XLSX: id → room → orgID
  if (isHolidayInn) {
    const { id, room, orgID } = resolveHolidayInnParams(row);
    const params = new URLSearchParams();
    params.set('id', id);
    if (room) params.set('room', room);
    if (orgID) params.set('orgID', orgID);
    const urlKiosk = row?.url_kiosk ?? row?.urlKiosk;
    if (urlKiosk != null && String(urlKiosk).trim() !== '') params.set('kiosk', String(urlKiosk));
    return `${base}?${params.toString()}`;
  }

  // 3) Default:
  //    - Legacy hotely (Angelo displeje, Nymburk, Fairmont): XLSX long URL
  //      ?room=X&orgID=Y&id=N
  //    - Nové orgy: krátký URL ?id=N&room=X (bez orgID — backend dohledá z device.id)
  const params = new URLSearchParams();
  const urlId = row?.url_id != null ? String(row.url_id).trim() : '';
  const urlRoom = row?.url_room != null ? String(row.url_room).trim() : '';
  const urlOrg = row?.url_orgID != null ? String(row.url_orgID).trim() : '';
  const urlKiosk = row?.url_kiosk != null ? String(row.url_kiosk).trim() : '';
  const isDisplay = dg !== 2;
  const idVal = urlId || (isDisplay ? '101' : '100');
  const roomVal = urlRoom || String(row?.id || '');
  const isLegacy = LEGACY_ORG_IDS.includes(normOrg);
  if (isLegacy) {
    params.set('room', roomVal);
    if (urlOrg) params.set('orgID', urlOrg);
    else if (orgId) params.set('orgID', String(orgId));
    params.set('id', idVal);
  } else {
    params.set('id', idVal);
    params.set('room', roomVal);
    if (urlOrg) params.set('orgID', urlOrg);
  }
  if (urlKiosk) params.set('kiosk', urlKiosk);
  return `${base}?${params.toString()}`;
};

const formatLastSeen = (ts) => {
  if (ts == null || typeof ts !== "number") return "—";
  const d = new Date(ts);
  const now = Date.now();
  const diffMin = Math.floor((now - ts) / 60000);
  if (diffMin < 1) return "právě teď";
  if (diffMin < 60) return `před ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `před ${diffH} h`;
  return d.toLocaleString("cs-CZ", { dateStyle: "short", timeStyle: "short" });
};

const GRID_SERVIS_ALL = "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 80px) 100px 160px minmax(200px, auto)";
const GRID_SERVIS_ORG = "minmax(0, 1fr) minmax(0, 80px) 100px 160px minmax(200px, auto)";

const SortableHeader = ({ column, children }) => (
  <span
    role="button"
    tabIndex={0}
    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && column.toggleSorting(column.getIsSorted() === "asc")}
    style={{ cursor: "pointer", userSelect: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
  >
    {children}
    {column.getIsSorted() === "asc" && " ↑"}
    {column.getIsSorted() === "desc" && " ↓"}
  </span>
);

const Servis = (props) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterName, setFilterName] = useState("");
  const [sorting, setSorting] = useState([{ id: "isAlive", desc: false }]);
  const showAll = !props.org;

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const url = showAll ? `${path}/devicesStatusAll` : `${path}/devicesStatus/${props.org}`;
      const res = await axios.get(url);
      let arr = Array.isArray(res.data) ? res.data : [];

      // Holiday Inn — přidat 3 synthetic "systémové" screeny (Restaurace
      // menu/spec, Recepce totem). Nejsou v cDevice, takže devicesStatus
      // je sám nevrátí — admin Servis tab je teď uvidí spolu s URL ke
      // kopírování / otevření. Heartbeat (isAlive / lastSeen) nemají.
      const normOrg = String(props.org || "").replace(/[{}]/g, "").toUpperCase();
      const showHiSynthetics = (!showAll && normOrg === HOLIDAY_INN_ORG_ID) ||
        // V showAll mode taky — admin co kouká na všechny orgy uvidí i tyhle
        // systémové screeny HI. Detekce: pokud arr obsahuje aspoň 1 zařízení HI,
        // jsou tu HI devicy v seznamu → přidat i synthetics nad ně.
        (showAll && arr.some(d => String(d?.cOrganization_id || '').replace(/[{}]/g, '').toUpperCase() === HOLIDAY_INN_ORG_ID));
      if (showHiSynthetics) {
        const orgIdForSyn = HOLIDAY_INN_ORG_ID;
        const synthetics = [
          { id: 'hi-restaurant-menu',       description: 'Restaurace - menu',              device_group: 1, url_id: 1, url_room: null, url_orgID: null,              cOrganization_id: orgIdForSyn, organizationName: 'Holiday Inn', _synthetic: true, isAlive: null, lastSeen: null },
          { id: 'hi-restaurant-speciality', description: 'Restaurace - speciality',        device_group: 1, url_id: 9, url_room: null, url_orgID: null,              cOrganization_id: orgIdForSyn, organizationName: 'Holiday Inn', _synthetic: true, isAlive: null, lastSeen: null },
          { id: 'hi-totem-3',               description: 'Obrazovka - recepce - na výšku', device_group: 1, url_id: 3, url_room: null, url_orgID: HOLIDAY_INN_ORG_ID, cOrganization_id: orgIdForSyn, organizationName: 'Holiday Inn', _synthetic: true, isAlive: null, lastSeen: null },
        ];
        arr = [...synthetics, ...arr];
      }

      // Skrýt virtuální místnosti bez fyzického displeje — v Servisu nedává
      // smysl monitorovat něco, co fyzicky neexistuje.
      const VIENNA_HOUSE_ORG_ID = '62BDEE7F-9CE9-41BE-A80C-EF69F045D56C';
      const isVirtualDevice = (row) => {
        const orgUpper = String(row?.cOrganization_id || '').replace(/[{}]/g, '').toUpperCase();
        const desc = (row?.description || '').toLowerCase().trim();
        if (orgUpper === HOLIDAY_INN_ORG_ID) {
          return ['meeting room e','meeting room f','meeting room pop up','orangerie','foyer'].includes(desc);
        }
        if (orgUpper === VIENNA_HOUSE_ORG_ID) {
          return ['opal a','opal b','opal c','opal d','restaurace','safir','smaragd','topaz'].includes(desc);
        }
        return false;
      };
      arr = arr.filter((row) => row._synthetic || !isVirtualDevice(row));

      setList(arr);
    } catch (e) {
      console.error("Servis fetch:", e);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [props.org]);

  useEffect(() => {
    // Polling rychleji (5s) — admin po smazání / přidání displeje rychle uvidí změnu.
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, [props.org]);

  const columns = useMemo(() => {
    const cols = [];
    if (showAll) {
      cols.push({
        id: "organizationName",
        accessorFn: (r) => r.organizationName || r.cOrganization_id || "—",
        header: ({ column }) => (
          <SortableHeader column={column}>Organizace</SortableHeader>
        ),
        enableSorting: true,
      });
    }
    cols.push(
      {
        id: "description",
        accessorFn: (r) => r.description || `#${r.id}`,
        header: ({ column }) => (
          <SortableHeader column={column}>Name</SortableHeader>
        ),
        enableSorting: true,
        sortingFn: (rowA, rowB) =>
          (rowA.original.description || "").localeCompare(rowB.original.description || "", "cs"),
      },
      {
        id: "device_group",
        accessorFn: (r) => (r.device_group === 1 ? "Displej" : "Kiosk"),
        header: ({ column }) => (
          <SortableHeader column={column}>Type</SortableHeader>
        ),
        enableSorting: true,
      },
      {
        id: "isAlive",
        accessorFn: (r) => (r.isAlive ? 1 : 0),
        header: ({ column }) => (
          <SortableHeader column={column}>Stav</SortableHeader>
        ),
        enableSorting: true,
        sortingFn: (rowA, rowB) => (rowA.original.isAlive ? 1 : 0) - (rowB.original.isAlive ? 1 : 0),
      },
      {
        id: "lastSeen",
        accessorKey: "lastSeen",
        header: ({ column }) => (
          <SortableHeader column={column}>Naposledy aktivní</SortableHeader>
        ),
        enableSorting: true,
        sortingFn: (rowA, rowB) => (rowA.original.lastSeen ?? 0) - (rowB.original.lastSeen ?? 0),
      },
      {
        id: "actions",
        header: "Akce",
        enableSorting: false,
        cell: () => null,
      }
    );
    return cols;
  }, [showAll]);

  const table = useReactTable({
    data: list,
    columns,
    state: {
      sorting,
      globalFilter: filterName,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const q = (filterValue || "").trim().toLowerCase();
      if (!q) return true;
      const name = (row.original.description || "").toLowerCase();
      const org = (row.original.organizationName != null ? String(row.original.organizationName) : "").toLowerCase();
      return name.includes(q) || org.includes(q);
    },
  });

  const openDevice = (row) => {
    const base = getDisplayBaseUrl();
    const url = buildDeviceUrl(base, row);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyUrl = async (row) => {
    const base = getDisplayBaseUrl();
    const url = buildDeviceUrl(base, row);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        alert("URL zkopírována do schránky.");
      }
    } catch (e) {
      console.error(e);
    }
  };

  const gridColumns = showAll ? GRID_SERVIS_ALL : GRID_SERVIS_ORG;
  const headerGroup = table.getHeaderGroups()[0];
  const rows = table.getRowModel().rows;
  const colCount = headerGroup?.headers.length ?? 0;

  return (
    <div className={classes.main}>
      <div className={classes.buttons}>
        <Button onClick={fetchStatus} disabled={loading} color="primary" variant="outlined">
          Obnovit
        </Button>
        <TextField
          size="small"
          placeholder="Filtrovat podle názvu..."
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          sx={{ minWidth: 220 }}
          inputProps={{ "aria-label": "Filtrovat podle názvu" }}
        />
      </div>
      <div className={classes.table}>
        <div
          className={classes.tableGridWrapper}
          style={{ gridTemplateColumns: gridColumns }}
        >
          <div style={{ display: "contents" }}>
            {headerGroup?.headers.map((header, i) => (
              <div
                key={header.id}
                className={`${classes.cellHeader} ${i === 0 ? classes.cellColFirst : ""} ${i === colCount - 1 ? classes.cellColLast : ""} ${header.column.id === "actions" ? classes.columnAct : classes.column}`}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </div>
            ))}
          </div>
          {loading && (
            <div className={classes.loader} style={{ gridColumn: "1 / -1" }}>
              <CircularProgress size={40} />
            </div>
          )}
          {!loading && list.length === 0 && (
            <div className={classes.empty} style={{ gridColumn: "1 / -1" }}>
              Žádné terminály
            </div>
          )}
          {!loading && list.length > 0 && rows.length === 0 && (
            <div className={classes.empty} style={{ gridColumn: "1 / -1" }}>
              Žádný výsledek pro „{filterName.trim()}“
            </div>
          )}
          {!loading &&
            rows.map((row, rowIndex) => (
              <div key={row.id} style={{ display: "contents" }}>
                {row.getVisibleCells().map((cell, i) => {
                  const isFirst = i === 0;
                  const isLast = i === colCount - 1;
                  const isLastRow = rowIndex === rows.length - 1;
                  const cellClasses = `${classes.cellData} ${isFirst ? classes.cellColFirst : ""} ${isLast ? classes.cellColLast : ""} ${isLastRow ? classes.cellRowLast : ""} ${cell.column.id === "actions" ? classes.columnAct : classes.column}`;
                  if (cell.column.id === "actions") {
                    return (
                      <div key={cell.id} className={cellClasses}>
                        <Button size="small" onClick={() => openDevice(row.original)} variant="outlined" color="primary">
                          Otevřít
                        </Button>
                        <Button size="small" onClick={() => copyUrl(row.original)} variant="outlined" color="primary">
                          URL
                        </Button>
                      </div>
                    );
                  }
                  let content = flexRender(cell.column.columnDef.cell, cell.getContext());
                  const isSynthetic = Boolean(row.original._synthetic);
                  if (cell.column.id === "isAlive" && cell.getValue() !== undefined) {
                    if (isSynthetic) {
                      // Synthetic systémový screen — heartbeat se neeviduje.
                      content = <span style={{ color: "#888" }}>Systémový</span>;
                    } else {
                      content = row.original.isAlive ? (
                        <span style={{ color: "var(--success, #2e7d32)" }}>Běží</span>
                      ) : (
                        <span style={{ color: "var(--error, #c62828)" }}>Neaktivní</span>
                      );
                    }
                  }
                  if (cell.column.id === "lastSeen") {
                    content = isSynthetic ? "—" : formatLastSeen(row.original.lastSeen);
                  }
                  return (
                    <div key={cell.id} className={cellClasses}>
                      {content}
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Servis;
